import type { As400ExportRow } from '@erp/rpc';
import {
  attachItemSiblings,
  isBlocking,
  rowModeOf,
  rowViolations,
  type CellRow,
  type CellRowLookups,
  type CellRulesByField,
  type CellViolation,
} from '@erp/item-master-rules';
import {
  asCellText,
  persistedCellValue,
  revertCellValue,
} from './as400ExportParts';
import type {
  As400GridRecord,
  PendingEditsByRowKey,
  PendingRowEdit,
} from './as400ExportEditState';

/**
 * The grid's side of the cell rules (the engine is @erp/item-master-rules,
 * shared with the server's save path): how a grid record and an unsaved row
 * edit read as a {@link CellRow}, and the blocking violations among a scope's
 * unsaved edits that Save refuses.
 */

/**
 * A grid record as a rule reads it: the cells it shows, edited where they
 * differ from what is persisted — the same test the pending tint uses.
 */
export function gridRecordCellRow(record: As400GridRecord): CellRow {
  const value = (field: string) => asCellText(record[field]);
  return {
    value,
    // A cell put back to its AS/400 value is a reset, not a new value: Save
    // drops the saved edit, so the rules have nothing of the author's to check
    // (the AS/400's own value may predate them).
    edited: (field) =>
      value(field) !== (record.__persisted[field] ?? null) &&
      !(field in record.__revert && value(field) === record.__revert[field]),
    as400Value: (field) => record.__revert[field] ?? null,
    mode: rowModeOf(record.__key),
    itemAdds: record.__itemAdds,
    stockUoms: record.__stockUoms,
  };
}

/**
 * An unsaved row edit as a rule reads it: its typed values over the row it was
 * made against (persisted values). A draft row has no row behind it, so its
 * untyped cells read as blank.
 */
export function pendingEditCellRow(
  edit: PendingRowEdit,
  row: As400ExportRow | undefined,
  lookups?: CellRowLookups,
): CellRow {
  return {
    value(field) {
      if (field in edit.values) return edit.values[field] ?? null;
      return row ? persistedCellValue(row, field) : null;
    },
    // As on the grid (gridRecordCellRow): a value put back to the cell's
    // AS/400 value is a reset, which Save drops, not a value to check.
    edited: (field) =>
      field in edit.values &&
      !(row && (edit.values[field] ?? null) === revertCellValue(row, field)),
    mode: rowModeOf(edit.rowKey),
    itemAdds: lookups?.itemAdds,
    stockUoms: lookups?.stockUoms,
  };
}

/** One violation on one unsaved row edit. */
export interface PendingCellViolation {
  rowKey: string;
  violation: CellViolation;
}

export function pendingCellRows(
  pending: PendingEditsByRowKey,
  rowFor: (rowKey: string) => As400ExportRow | undefined,
  lookups?: CellRowLookups,
): ReadonlyMap<string, CellRow> {
  const rows = new Map<string, CellRow>();
  for (const [key, edit] of pending) {
    rows.set(key, pendingEditCellRow(edit, rowFor(key), lookups));
  }
  return attachItemSiblings(rows);
}

/**
 * Every blocking violation among a scope's unsaved edits, in edit order —
 * what Save refuses (warnings don't stop it). `rowFor` finds the row an edit
 * was made against.
 */
export function pendingViolations(
  pending: PendingEditsByRowKey,
  rules: CellRulesByField,
  rowFor: (rowKey: string) => As400ExportRow | undefined,
  lookups?: CellRowLookups,
): PendingCellViolation[] {
  if (rules.size === 0) return [];
  const cellRows = pendingCellRows(pending, rowFor, lookups);
  return [...pending.values()].flatMap((edit) => {
    const row = cellRows.get(edit.rowKey);
    if (!row) return [];
    return rowViolations(rules, row).flatMap((violation) =>
      isBlocking(violation) ? [{ rowKey: edit.rowKey, violation }] : [],
    );
  });
}
